import React from 'react';
import { isStudioScopedRole } from '../utils/roles';
import { Box, Button, Stack, Typography } from '@mui/material';
import { Lock, WhatsApp } from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { SWEATSOCIAL_WHATSAPP } from './whatsapp/WhatsAppHub';

/**
 * Pages a studio login can see in the sidebar but can't use until SweatSocial
 * sets something up for them. The sidebar dims the item and the page shows a
 * "contact SweatSocial" screen instead — both from `getFeatureLock`, so they
 * can never disagree. Super admins are never locked.
 */
type LockReason = 'engagement' | 'healthForms' | 'merchandise';

/** Push notifications go out through the studio's own app — no app, no push. */
export function isPushLocked(admin: any): boolean {
  const role = admin?.role ?? '';
  if (!isStudioScopedRole(role)) return false;
  return !admin?.studio?.whitelabelConfig?.appSource;
}

export function getFeatureLock(admin: any, path: string): LockReason | null {
  const role = admin?.role ?? '';
  if (!isStudioScopedRole(role)) return null;

  const studio = admin?.studio ?? {};
  const wl = studio.whitelabelConfig ?? {};
  const perms = studio.studioPermissions ?? {};
  const hasApp = !!wl.appSource;

  switch (path) {
    // Greys the sidebar item only when BOTH channels are unavailable. The page
    // itself isn't gated — NotificationsPage shows a locked message per channel.
    case '/notifications':
      return hasApp || perms.enableMessaging === true ? null : 'engagement';
    // Forms are filled in the studio's app or on its booking website
    // (studio-web, found by bookingSlug/bookingDomain) — either one counts.
    case '/health-forms':
      return hasApp || !!wl.bookingSlug || !!wl.bookingDomain ? null : 'healthForms';
    case '/merchandise':
      return perms.enableMerchandise === true ? null : 'merchandise';
    default:
      return null;
  }
}

const COPY: Record<LockReason, { title: string; body: string; ask: string }> = {
  engagement: {
    title: 'Push notifications need your studio’s own app',
    body:
      'Push notifications land on your members’ phones through your studio’s own branded app. ' +
      'Get your app with SweatSocial to start sending them from here.',
    ask: 'I’d like to get our own studio app so we can send push notifications',
  },
  healthForms: {
    title: 'Health forms need your studio’s own app',
    body:
      'Members fill in their health and injury details in your studio’s own app before class, ' +
      'and you review them here. Get your app with SweatSocial to start collecting health forms.',
    ask: 'I’d like to get our own studio app so we can collect health forms',
  },
  merchandise: {
    title: 'Merchandise isn’t enabled yet',
    body:
      'Sell your studio’s merchandise to members and track every order from here. ' +
      'Contact SweatSocial to enable merchandise for your studio.',
    ask: 'I’d like to enable merchandise',
  },
};

const FeatureLocked: React.FC<{ reason: LockReason; studioName?: string }> = ({ reason, studioName }) => {
  const copy = COPY[reason];
  const text = `Hi SweatSocial! ${copy.ask} for ${studioName || 'my studio'}.`;
  const href = `https://wa.me/${SWEATSOCIAL_WHATSAPP}?text=${encodeURIComponent(text)}`;
  return (
    <Box
      sx={{
        borderRadius: 4,
        p: { xs: 3, md: 5 },
        textAlign: 'center',
        bgcolor: '#fff',
        border: '1px solid rgba(0,0,0,0.08)',
      }}
    >
      <Stack spacing={2} alignItems="center" sx={{ maxWidth: 520, mx: 'auto' }}>
        <Box
          sx={{
            width: 64,
            height: 64,
            borderRadius: 3,
            bgcolor: 'rgba(233,109,154,0.12)',
            display: 'grid',
            placeItems: 'center',
          }}
        >
          <Lock sx={{ color: '#e96d9a', fontSize: 34 }} />
        </Box>
        <Typography variant="h5" sx={{ fontWeight: 800 }}>{copy.title}</Typography>
        <Typography color="text.secondary">{copy.body}</Typography>
        <Button
          variant="contained"
          startIcon={<WhatsApp />}
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          sx={{ bgcolor: '#25D366', color: '#fff', fontWeight: 800, borderRadius: 2, '&:hover': { bgcolor: '#1ebe5a' } }}
        >
          Contact SweatSocial
        </Button>
      </Stack>
    </Box>
  );
};

/** Renders the page, or the locked screen when this login can't use it yet. */
export const FeatureGate: React.FC<{ path: string; children: React.ReactNode }> = ({ path, children }) => {
  const { admin } = useAuth();
  const reason = getFeatureLock(admin, path);
  if (!reason) return <>{children}</>;
  return <FeatureLocked reason={reason} studioName={(admin?.studio as any)?.studioName} />;
};

export default FeatureLocked;
